import { useMemo, useRef } from 'react'

import { Button } from 'components/Button'
import { lineStrings } from 'constants/strings'
import { ERichMenuImageFieldLayout, RICH_MENU_IMAGE_ACCEPT } from 'types'

const strings = lineStrings.management.richMenu.create

type RichMenuImageFieldProps = {
  onFileChange: (file: File | null) => void
  layout?: ERichMenuImageFieldLayout
  error?: string
  disabled?: boolean
  hint?: string
}

export function RichMenuImageField({
  onFileChange,
  layout = ERichMenuImageFieldLayout.Stacked,
  error,
  disabled = false,
  hint,
}: RichMenuImageFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const triggerFile = () => inputRef.current?.click()

  const fileInput = useMemo(() => (
    <input
      ref={inputRef}
      type="file"
      accept={RICH_MENU_IMAGE_ACCEPT}
      disabled={disabled}
      className="hidden"
      onChange={(e) => {
        const file = e.target.files?.[0] ?? null
        onFileChange(file)
        e.target.value = ''
      }}
    />
  ), [disabled, onFileChange])

  if (layout === ERichMenuImageFieldLayout.Row) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-1">
        <div className="flex w-full items-center gap-4">
          {fileInput}
          <span className="text-sm font-medium text-ink">{strings.imageShortLabel}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={triggerFile}
            disabled={disabled}
          >
            {strings.imageSelectButton}
          </Button>
        </div>
        {hint ? <p className="text-xs text-muted">{hint}</p> : null}
        {error ? (
          <p className="text-xs text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {fileInput}
      <span className="text-xs text-muted">{strings.imageLabel}</span>
      {hint ? <span className="text-xs text-muted">{hint}</span> : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        onClick={triggerFile}
        disabled={disabled}
      >
        {strings.selectImageButton}
      </Button>
      {error ? (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
